<script setup>
  import { ref, onMounted, computed } from 'vue';
  import store from '../store';
  import ReservationData from '../reservation-data.js';
  import trackEvent from '../lib/atd-track-event.js';
  import SmsDisclaimer from './SmsDisclaimer.vue';

  const errors = ref({});
  const recommended_brands = ref([]);
  const formIsSubmitting = ref(false);
  const reservationData = ReservationData.data;

  const defaultHeading = 'Thank you for your submission. We look forward to seeing you at the Cox Automotive Village at NADA 2025.';
  const defaultSubheading = 'We find that dealers like you can also benefit from integrations with our other brands. Select below to learn more:';

  const reccomendedBrandsList = computed(() => {
    let goal_brands = [];
    for(const brand_slug of Object.keys(reservationData.selections)) {
      const products = reservationData.selections[brand_slug]?.products || [];
      for(const product of products) {
        if(product.recommended_brands) {
          goal_brands = goal_brands.concat(product.recommended_brands);
        }
      }
    }
    
    let brands_to_show = goal_brands.length ?  goal_brands : store.brand.recommended_brands;

    return uniq(brands_to_show)
      .filter((brand) => brand.brand_value !== reservationData.BOI);
  });

  const recoBrandsLeft = computed(() => {
    return reccomendedBrandsList.value.slice(0, Math.ceil(reccomendedBrandsList.value.length / 2));
  });

  const recoBrandsRight = computed(() => {
    return reccomendedBrandsList.value.slice(Math.ceil(reccomendedBrandsList.value.length / 2));
  });

  const submitForm = (e) => {
    formIsSubmitting.value = true;
    errors.value = {};
    if(!recommended_brands.value.length) {
      errors.value['recommended_brands'] = 'Please select at least one option.';
      formIsSubmitting.value = false;
      return;
    }

    // Track reco submissions
    trackEvent(store.brand.tradeshow_name, store.brand.brand_name, 'Recommendation Submission');

    reservationData['recommended_brands'] = recommended_brands.value;

    ReservationData.sendReservation({...reservationData, selections: []}, null, true);
  }

  onMounted(async () => {
    if((!store.submissionId || !reccomendedBrandsList.value.length) || (store.brand.show_recos_on_step_one && !reservationData['recommended_brands'].length)) {
      ReservationData.goToConfirmation({...reservationData});
    }

    if(store.brand.show_recos_on_step_one && reservationData['recommended_brands'].length) {
      formIsSubmitting.value = true;
      ReservationData.sendReservation({...reservationData, submissionId: store.submissionId, selections: []}, null, true)
        .then(() => {
          formIsSubmitting.value = false;
        });
      return;
    }
  })

  function uniq(source) {
    if (!Array.isArray(source)) {
      return [];
    }
    return [...new Set(source.map(o => {
      const sortedObjectKeys = Object.keys(o).sort();
      const obj = Object.assign({}, ...sortedObjectKeys.map(k => ({[k]: o[k]})));
      return JSON.stringify(obj);
    }))]
    .map(s => JSON.parse(s));
  }
</script>

<template>
  <p class="text-center" v-if="store.brand.show_recos_on_step_one && formIsSubmitting"><strong>
    Please wait you will be redirected to the confirmation page.
  </strong></p>
  
  <div v-if="!store.brand.show_recos_on_step_one && reccomendedBrandsList.length" class="nada-reservation-form">
    <p class="text-center">{{store.brand?.recommendation_heading  || defaultHeading }}</p>

    <p class="text-center"><strong>{{store.brand?.recommendation_subheading  || defaultSubheading }}</strong></p>

    <form class="relative" @input="onAutoSave">
      <div class="loader loading-on"><span class="visuallyhidden">Loading</span></div>

      <div class="loading-off">
        <div class="demo-checks" :class="errors['Demos'] ? 'error': ''">
          <label>Which brands are you interested in?</label>
          
          <div class="checkbox-group">
            <div class="row">
              <div class="col-sm-6">
                <div v-for="brand in recoBrandsLeft" :key="brand.brand_value">
                  <label class="checkbox-label alt">
                    <input type="checkbox" 
                      name="selectedProducts"
                      v-model="recommended_brands"
                      :value="brand"
                    />
                    <span>{{brand.brand_name}}</span>
                  </label>
                </div>
              </div>  

              <div class="col-sm-6">
                <div v-for="brand in recoBrandsRight" :key="brand.brand_value">
                  <label class="checkbox-label alt">
                    <input type="checkbox" 
                      name="selectedProducts"
                      v-model="recommended_brands"
                      :value="brand"
                    />
                    <span>{{brand.brand_name}}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <span class="error-msg large" v-if="errors['recommended_brands']">{{errors['recommended_brands']}}</span>
      </div>

      <div class="form-section text-center">
        <div class="breathe">
          <div :class="formIsSubmitting ? 'loading' : ''">
            <button type="button" class="btn btn-inline relative form-submit-button" v-on:click="submitForm($event)" >
              <span class="loading-off">
                Reserve Now
                <span class="caret-right">&rsaquo;</span>
              </span> 
              <div class="loader loading-on"><span class="visuallyhidden">Loading</span></div>
            </button>
          </div>
        </div>

        <div v-if="recaptcha_error" class="breathe">
          <span class="error-msg large">Could not submit form, please try again later.</span>
        </div>

        <SmsDisclaimer />
      </div>
    </form>
  </div>
</template>
